import {
  getActivity,
  getActivityFields,
  getLoggableActivities,
} from '@kizen/api/activities';
import { QueryClient, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { ACTIVITIES } from 'queries/query-keys';
import AxiosService from 'services/AxiosService';

type Activity = Awaited<ReturnType<typeof getActivity>>;

type ActivityFields = Awaited<ReturnType<typeof getActivityFields>>;

type LoggableActivitiesResponse = Awaited<
  ReturnType<typeof getLoggableActivities>
>;

export const removeActivityQuery = (
  queryClient: QueryClient,
  activityId: string
) => {
  queryClient.removeQueries({ queryKey: ACTIVITIES.ACTIVITY(activityId) });
};

export const removeActivityFieldsQuery = (
  queryClient: QueryClient,
  activityId: string
) => {
  queryClient.removeQueries({
    queryKey: ACTIVITIES.ACTIVITY_FIELDS(activityId),
  });
};

export const useActivityQuery = <TData = Activity>(
  activityId: string,
  options: Omit<
    UseQueryOptions<Activity, unknown, TData>,
    'queryKey' | 'queryFn'
  > = {}
) => {
  return useQuery({
    queryKey: ACTIVITIES.ACTIVITY(activityId),
    queryFn: async () => {
      return getActivity(AxiosService, activityId);
    },
    ...options,
  });
};

export const useLoggableActivitiesQuery = <TData = LoggableActivitiesResponse>({
  params,
  queryOptions,
}: {
  params?: Required<Parameters<typeof getLoggableActivities>>['1']['params'];
  queryOptions?: Omit<
    UseQueryOptions<LoggableActivitiesResponse, unknown, TData>,
    'queryKey' | 'queryFn'
  >;
} = {}) => {
  return useQuery({
    queryKey: ACTIVITIES.LOGGABLE(params),
    queryFn: async () => {
      return getLoggableActivities(AxiosService, { params });
    },
    ...queryOptions,
  });
};

export const useActivityFieldsQuery = <TData = ActivityFields>(
  activityId: string,
  options: Omit<
    UseQueryOptions<ActivityFields, unknown, TData>,
    'queryKey' | 'queryFn'
  > = {}
) => {
  return useQuery({
    queryKey: ACTIVITIES.ACTIVITY_FIELDS(activityId),
    queryFn: async () => {
      return getActivityFields(AxiosService, activityId);
    },
    ...options,
  });
};
